<template>
    <v-app>
        <v-app-bar color="primary" position="fixed">
            <v-app-bar-nav-icon @click.stop="drawer = !drawer" v-if="isAuthed"></v-app-bar-nav-icon>
            <div class="d-flex align-center ml-2"></div>
            <v-app-bar-title data-testId="ap-toolbar-title">Intellitask Admin Dashboard</v-app-bar-title>
            <v-spacer></v-spacer>

            <v-select
                :items="environmentsList"
                label="Environment"
                v-model="environment"
                @update:model-value="updateEnvironment()"
                v-if="isAuthed"
                class="application-select-box"
            ></v-select>

            <v-btn variant="text" @click="signOut()" v-if="isAuthed">Sign out</v-btn>
        </v-app-bar>
        <v-navigation-drawer position="fixed" v-model="drawer" v-if="isAuthed">
            <v-list>
                <v-list-item link :class="{ 'active-item': selectedTab === 'sessions-viewer' }" @click="selectedTab = 'sessions-viewer'">
                    <template v-slot:prepend>
                        <v-icon icon="mdi-weather-night"></v-icon>
                    </template>
                    <v-list-item-title>Sessions Viewer</v-list-item-title>
                </v-list-item>
                <v-list-item link :class="{ 'active-item': selectedTab === 'triage-report' }" @click="selectedTab = 'triage-report'">
                    <template v-slot:prepend>
                        <v-icon icon="mdi-menu"></v-icon>
                    </template>
                    <v-list-item-title>Triage Report</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-navigation-drawer>
        <v-main class="bg-light-grey">
            <v-container justify-center align-center vertical-align-center v-if="!isAuthed">
                <div class="auth-container">
                    <auth v-on:auth="loginComplete" v-on:error="handleError"></auth>
                </div>
            </v-container>
            <v-container v-if="isAuthed" class="px-0">
                <SessionsViewer v-show="selectedTab == 'sessions-viewer' && isAuthed" ref="sessionsViewer" />
                <TriagedReport v-show="selectedTab == 'triage-report' && isAuthed" ref="triageReport"></TriagedReport>
            </v-container>
        </v-main>
        <v-snackbar v-model="hasError" color="secondary" class="gray--text" location="top" :timeout="5" :multi-line="true">
            {{ errorMessage }}
            <v-btn variant="text" color="accent" class="mr-2" @click="dismissError()">Dismiss</v-btn>
        </v-snackbar>
    </v-app>
</template>

<script lang="ts">
import Cookies from 'js-cookie';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import auth from './components/auth.vue';
import SessionsViewer from './components/SessionsViewer.vue';
import TriagedReport from './components/TriagedReport.vue';
import { listenForAuthErrors, setAuthHeader } from '@/services/http';
import * as CONFIG from './utils/config';
import { trim } from 'lodash';
let gCognitoUser = null;

const config = {
    UserPoolId: CONFIG.paramCognitoPoolId,
    ClientId: CONFIG.paramCognitoClientId,
    Storage: new AmazonCognitoIdentity.CookieStorage(CONFIG.cookieStorageSettings)
};

export default {
    name: 'App',
    components: {
        auth,
        SessionsViewer,
        TriagedReport
    },
    data: () => ({
        drawer: true,
        tab: '',
        selectedTab: 'sessions-viewer',
        isAuthed: false,
        environmentsList: ['dev1', 'devrc', 'qa', 'prod'],
        environment: undefined,
        errorMessage: null,
        keepAliveTimeout: null
    }),
    methods: {
        handleError: function (errorMessage: string) {
            this.errorMessage = errorMessage;
        },
        dismissError: function () {
            this.errorMessage = null;
        },
        updateEnvironment: function () {
            console.log(`Set environment to : ${this.environment}`);
            localStorage.setItem('environment', this.environment);
            if (this.selectedTab === 'sessions-viewer') {
                this.$refs.sessionsViewer.refreshEnvironment();
                this.$refs.sessionsViewer.clearOldData();
            } else if (this.selectedTab === 'triage-report') {
                this.$refs.triageReport.refreshEnvironment();
                this.$refs.triageReport.environmentChanged();
            }
        },
        setAuth: function (token: string) {
            sessionStorage.token = token;
            setAuthHeader(sessionStorage.token);
            this.isAuthed = true;
        },
        loadCurrentUser: async function () {
            const userPool = new AmazonCognitoIdentity.CognitoUserPool(config);
            const cognitoUser = userPool.getCurrentUser();
            if (cognitoUser != null) {
                gCognitoUser = cognitoUser;
                const session = await this.getCurrentOrNewSession();
                if (!session) {
                    console.log('Current session is invalid.');
                    this.isAuthed = false;
                    return;
                }
                cognitoUser.getUserAttributes((err, result) => {
                    if (err) {
                        this.signOutCognitoUser();
                    } else if (!result) {
                        this.signOutCognitoUser();
                    } else {
                        let userName = '';
                        const givenNameAttr = result.find((attribute) => attribute.getName() === 'given_name');
                        if (givenNameAttr) {
                            userName = givenNameAttr.getValue();
                        }
                        const familyNameAttr = result.find((attribute) => attribute.getName() === 'family_name');
                        if (familyNameAttr) {
                            userName = userName + ' ' + familyNameAttr.getValue();
                        }
                        localStorage.setItem('userName', trim(userName));
                        this.keepAwsSessionAlive();
                    }
                });
            }
        },
        getCurrentOrNewSession: async function () {
            const that = this;
            return new Promise((resolve) => {
                let currentSession = null;
                gCognitoUser.getSession((err, session) => {
                    if (err) {
                        console.error('getSession err, signing out...', err);
                        that.signOutCognitoUser();
                    } else {
                        if (session.isValid()) {
                            that.setAuth(session.idToken.jwtToken);
                            currentSession = session;
                        } else {
                            console.log('session was not valid, signing out...');
                            that.signOutCognitoUser();
                        }
                    }
                    resolve(currentSession);
                }, {});
            });
        },
        signOutCognitoUser() {
            sessionStorage.clear();
            localStorage.clear();
            gCognitoUser.globalSignOut({
                onSuccess: (msg: string) => {
                    //Cognito does not remove its cookies after a sign out, which will cause a failure if you try to sign in again after signing out
                    const cookies = Cookies.get();
                    const cookieNames = Object.keys(cookies);
                    for (const cookieName of cookieNames) {
                        if (cookieName.startsWith('Cognito')) {
                            Cookies.remove(cookieName);
                        }
                    }
                },
                onFailure: (err: Error) => {
                    // Not implemented
                    console.error(err);
                }
            });
            this.isAuthed = false;
        },
        loginComplete: async function () {
            await this.loadCurrentUser(true);
        },
        signOut: async function () {
            // Save lastPageSize to local variable before clearing the local storage
            const itemsPerPage = parseInt(localStorage.getItem('lastPageSize'), 10);
            const environment = localStorage.getItem('environment');
            sessionStorage.clear();
            localStorage.clear();
            // Sign out lambda help to log out from the Cognito user pool.
            // For local dev environment, we can't use sign out lambda@edge function to sign out,
            // as it is invoked based on the CloudFront distribution domain.
            this.signOutCognitoUser();
            // Persist the lastPageSize to localStorage again after clearing local storage
            if (!isNaN(itemsPerPage)) {
                localStorage.setItem('lastPageSize', itemsPerPage.toString());
            }
            localStorage.setItem('environment', environment);
        },
        keepAwsSessionAlive() {
            clearInterval(this.keepAliveTimeout);
            this.keepAliveTimeout = setInterval(async () => {
                // getCurrentOrNewSession function will refresh session or logout user if it can't
                const session = await this.getCurrentOrNewSession();
                if (!session) {
                    this.isAuthed = false;
                }
            }, 900000); // Refresh session every 15 mins
        }
    },
    computed: {
        hasError: function () {
            return this.errorMessage != null;
        }
    },
    mounted: async function () {
        await this.loadCurrentUser(false);
        const linkParts = location.hash.split('/');
        if (linkParts.length > 1 && linkParts[1].length > 0) {
            const env = linkParts[1];
            this.environment = env;
            this.updateEnvironment();
        } else if (localStorage.getItem('environment')) {
            this.environment = localStorage.getItem('environment');
        } else {
            this.environment = 'dev1';
            this.updateEnvironment();
        }

        // Handle 401 error - signout current session to restart auth flow
        listenForAuthErrors(() => this.signOut());
    }
};
</script>

<style lang="scss" scoped>
.application-select-box {
    max-width: 300px;
    max-height: 55px;
    background-color: #222222;
}
.auth-container {
    max-width: 440px;
    padding-top: 10vh;
    margin: 0 auto;
}
.logo {
    border-radius: 50%;
}
.v-text-field__details {
    display: none;
}
.active-item {
    background-color: #efefef;
}
</style>
